import React, { useCallback } from 'react';
import styled from 'styled-components';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { useServiceCollections } from 'shared/apollo/pageHooks/useAllServiceCollections';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import { serviceDefinitions } from 'npo/pages/ReportBuilder/Components/ImpactSummary/serviceDefinitions';
import SelectorField from './SelectorField';
import {
  AnswerInput,
  HelperText,
  useProgram,
} from 'shared/providers/ProgramProvider';
import { serviceQuestionId } from 'shared/providers/IndicatorProvider/utils';
import { useBlockedSignal } from './useBlockedSignal';

interface ProgramSubServiceFieldProps {
  disabled?: boolean;
  questionId: number;
  value: AnswerInput;
  sectionKey: string | null | undefined;
  onChange: (next: AnswerInput) => void;
  onBlockedChange?: (blocked: boolean) => void;
}

const StyledDiv = styled.div`
  .service-collection-textfield {
    background-color: ${COLORS.whiteBase};
  }
`;

const ProgramSubServiceField: React.FC<ProgramSubServiceFieldProps> = ({
  sectionKey,
  questionId,
  value,
  disabled,
  onChange,
  onBlockedChange,
}) => {
  const state = useServiceCollections({});
  const { answers } = useProgram();
  const mainService = answers[serviceQuestionId]?.text;
  useBlockedSignal(!mainService, onBlockedChange);

  const handleSelect = useCallback(
    (next: string) => {
      onChange({ ...value, text: next, questionText: next });
    },
    [onChange, value],
  );

  if (state.type === 'REQUEST_INIT' || state.type === 'REQUEST_START') {
    return <TrueImpactLoading />;
  }

  if (state.type === 'REQUEST_ERROR') {
    return (
      <TrueImpactText variant="body-small" color="critical">
        {state.error?.message || 'Failed to load service collections.'}
      </TrueImpactText>
    );
  }

  if (!mainService) {
    return (
      <TrueImpactText color="critical" variant="body-small">
        Please answer the main service question first.
      </TrueImpactText>
    );
  }

  const mainCollection = state.data.find(x => x.name === mainService);
  const options = mainCollection?.services ?? [];

  if (options.length === 0) {
    return (
      <TrueImpactText color="critical" variant="body-small">
        No sub-services available for the selected service.
      </TrueImpactText>
    );
  }

  return (
    <StyledDiv>
      <HelperText sectionKey={sectionKey} questionId={questionId} />
      <SelectorField>
        <SelectorOptions
          options={options.map(o => o.name)}
          variant="radio"
          margin="0"
          maxWidth="100%"
          disabled={disabled}
          getLabel={name => name}
          getSelected={name => (value.text ?? '') === name}
          onClick={name => handleSelect(name)}
          tooltip={name =>
            (serviceDefinitions as Record<string, { description: string }>)[
              name
            ]?.description ?? ''
          }
        />
      </SelectorField>
    </StyledDiv>
  );
};

export default ProgramSubServiceField;
